import type { Backend as SheetsBackend, Option, Unsubscribe } from "@tento-lona/sheets";
import { DateTime, NaiveDate, SemanticTime, Utc } from "@tento-chrono";
import { TypedApi } from "@tento-core/typed-api";
import { ListenerErrors, type ListenerErrorSink, type Thrown } from "@tento-core/listener-errors";
import { LiveData } from "@tento-core/livedata";
import { RangeBasedFetcher } from "@tento-core/range-based-fetcher";
import type { Rational } from "@tento-core/rational";
import { Network } from "../network.ts";
import { CellId, RowId, SheetNetworkKey } from "@tento-lona/sheets";
import { type CellCardinality, IngestionGate, InstallPacer } from "@tento-lona/sheets/platform";
import type { CellEntry, TimepointCellEntry } from "@tento-lona/sheets";
import { cellAccessKey } from "../sync/cell-access-key.ts";
import { LookupKeyOwner } from "../client/lookup-key-owner.ts";
import { RowCellCache, type RowFetchCompletion } from "../sync/row-cell-cache.ts";
import { RowData } from "../sync/row-data.ts";
import { UtcDays } from "@tento-lona/sheets/internal-sdk";
import type { CellCachingBackend } from "../sync/sync-backend.ts";
import { postTimepointCell, postTimepointClear } from "../sync/sync-backend.ts";
import { RowKey } from "@tento-lona/sheets";
import { isCanonicalKey } from "@tento-lona/sheets/platform";
import { RowLocalId } from "@tento-lona/sheets";
import { DependencyGraph } from "../formula/dependency-graph.ts";
import { RowSyncState } from "../sync/sync-poll-entry.ts";
import { RowType, CellProvider, AliasResolver, type DataOwner } from "../rows/row-descriptor.ts";
import type { SheetRow } from "../rows/sheet-row.ts";
import { SheetId } from "@tento-lona/sheets";
import { RowNetworkKey } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { PageLifecycle } from "../page-lifecycle.ts";
import { Actor, type InvalidationSink } from "../invalidation.ts";
import { CellWriteError, SdkRequestError } from "../errors.ts";
import type { DebouncedRequests } from "@tento-core/debounced-requests";
import { DurableHandoffs } from "../client/durable-handoffs.ts";
import { afterFailedWrite, type AfterFailedWrite } from "../client/after-failed-write.ts";
import { requireNonEmptyKey } from "../sync/_contracts.ts";
import { TaggedCells } from "./tagged-cells.ts";
import { BulkInstall } from "./bulk-install.ts";
import { RowFetchPlan } from "./row-fetch-plan.ts";
import type { CellStoreView } from "./cell-store-view.ts";
import { SheetRowMeta } from "../rows/sheet-row-meta.ts";
import { Json, type CellValue, type JsonObject } from "@tento-lona/sheets";

export { CellSetCoordinator } from "./cell-set-coordinator.ts";
import { CellSetCoordinator } from "./cell-set-coordinator.ts";

/**
 * Cell data accessor for a sheet. Manages per-row RowHandles internally,
 * provides bulk fetch, cached reads, staleness, and change subscriptions.
 *
 * Owned by `SheetHandle`; consumers see its `SheetHandle.Cells` slice
 * (`handle.cells`), SDK modules the whole accessor through
 * `sheetHandleInternals(handle).cells`.
 */
export class CellAccessor {
  /**
   * Actor tagging the scoped per-row invalidation emitted when a per-row
   * fetch settles (`scheduleFetchSettleInvalidation`). Dedicated so
   * downstream consumers can suppress the settle echo by name.
   */
  static readonly FETCH_SETTLE_ACTOR = new Actor("cell-fetch-settle");

  /** A row type's cell cardinality (`RowType`): what a row cache's metadata is applied with. */
  static #typeCardinality(type: string): CellCardinality {
    return RowType.get(type).cellCardinality;
  }

  readonly #rowHandles = new Map<RowNetworkKey, RowCellCache>();
  /** Derived sources belong to this sheet, not the backend registry. */
  readonly #contextRows = new Set<RowCellCache>();
  /**
   * Reverse index `RowLocalId → the access key its handle is registered under`,
   * for the `cow-strand-blank` witness. O(1) — avoids scanning `rowHandles` on
   * every miss (initial sheet load is all-misses). Maintained on handle
   * creation and `rekeyHandles`; stale entries are harmless (the witness
   * re-checks `rowHandles.get(priorKey)` for a live, coverage-bearing handle).
   */
  readonly #handleKeyByLocalId = new Map<RowLocalId, string>();
  readonly #ensuredLookupRows = new Set<string>();
  readonly #ensureLookupRowPromises = new Map<string, Promise<void>>();
  readonly #listeners = new Set<CellAccessor.OriginChangeListener>();
  readonly #publicListeners = new Map<CellAccessor.ChangeListener, CellAccessor.OriginChangeListener>();
  #disposed = false;
  /** This accessor's liveness, as a failed write's recovery checks it
   *  (`afterFailedWrite`): none starts once `dispose` ran. */
  readonly #owner: AfterFailedWrite.Owner = {
    assertLive: (verb: string) => {
      if (this.#disposed) throw new Error(`CellAccessor.${verb}: the accessor was disposed`);
    },
  };
  #bulkFetchObserver: Option<CellAccessor.BulkFetchObserver> = null;
  #captureBulkFetchObserver: Option<(range: Option<NaiveDate.Range>) => CellAccessor.BulkFetchObservation> = null;
  /**
   * Whether this accessor's sheet is the live/visible one. The Sheet UI sets it
   * on bind — deactivate the outgoing sheet, activate the incoming one. When
   * false, async cell-arrival continuations bail before running their
   * (expensive) distribute → formula → listener cascade. That cascade, summed
   * across the sheets a rapid switch leaves behind, is the microtask avalanche
   * that freezes the main thread. See
   * `ongoing-sheet-performance.project.docs` Migration P2.
   */
  #isActive = true;
  /**
   * Set when a bulk fetch bailed because the sheet was inactive. The
   * `sheetFetcher` marks coverage optimistically at fetch START, so a bailed
   * fetch would otherwise leave the range "covered" but unapplied — stranding
   * the sheet stale. On reactivation we reset that coverage so the re-shown
   * sheet re-fetches fresh.
   */
  #bailedWhileInactive = false;

  /** The underlying Backend. */
  readonly #backend: CellCachingBackend;
  // Read by no path yet: the keyed cell read moves onto it.
  readonly #sheets: SheetsBackend;

  /** The owning sheet's registries — bound by `bindRegistries` after parse. */
  #graph: Option<DependencyGraph<RowLocalId>> = null;
  #ids: Option<RowLocalId.Registry> = null;
  /** The registry an unbound accessor's change notices brand their rows in
   *  (`#notifyChange`); `null` until one is needed. */
  #unboundIds: Option<RowLocalId.Registry> = null;
  #rows: Option<SheetRow.Registry> = null;
  /**
   * The owning sheet's rows-side-first resolvers (see
   * `CellAccessor.Resolvers`), bound with the registries; `null` for an
   * accessor no sheet has bound.
   */
  #resolvers: Option<CellAccessor.Resolvers> = null;

  /** What each row's cache fetches for a range (`RowFetchPlan`), reading this accessor through its port. */
  readonly #fetchPlan: RowFetchPlan;

  /**
   * Client invalidation sink, passed by `SheetHandle`'s constructor from
   * `deps.invalidation`. Carries the scoped per-row settle invalidation
   * (`scheduleFetchSettleInvalidation`) into the app dispatch. Null outside
   * a SheetHandle context — settles then stay subscription-only.
   */
  readonly #invalidation: Option<InvalidationSink>;

  /**
   * Sheet-level copy-on-write promote, injected by the owning
   * `SheetHandle`. A no-op once the sheet is `"synced"`; on an ephemeral sheet
   * it persists the sheet before the first cell write so the write doesn't
   * orphan against an un-promoted sheet id.
   */
  #ensurePromoted: Option<() => Promise<void>> = null;

  /** Sheet-level fetcher: padding, range caching, dedup, delta extension. */
  readonly #sheetFetcher = new RangeBasedFetcher<Utc>(async (range) =>
    this.#bulkFetch(range),
  );

  /**
   * The pause between the bulk response and its install (`IngestionGate`):
   * `OPEN` by default; the sheet UI holds it while the user is scrolling so a
   * response that lands mid-gesture distributes at release, not inside a
   * motion frame. Set once per bind by `setIngestionGate`.
   */
  readonly #ingestion = new IngestionGate.Slot();
  /** Optional admission budget shared with the rows-side model. */
  #installPacer: Option<InstallPacer> = null;

  /**
   * Bulk responses parked behind the gate, each holding a lease on the
   * exact handles it will install into (captured revisions, see
   * `RowCellCache.captureWriteRevision`). Released in `finally` on every exit
   * and by `dispose()`.
   */
  readonly #leases = new Set<CellAccessor.Lease>();

  /**
   * Bulk-fetch observations taken before their request went out and not
   * released yet (`#observe`): the sheet-wide read the rows' loads wait on,
   * the store revisions it holds. Released on every exit of their fetch and
   * by `dispose()`, so a request still out holds nothing past the accessor.
   */
  readonly #observations = new Set<CellAccessor.BulkFetchObservation>();

  /**
   * A range whose parked response could not be installed into every handle
   * (see `bulkFetch`): fetched again — as a real request — once the sheet
   * fetcher has no fetch in flight. Recorded in the fetch's `finally`, so a
   * failed continuation keeps the intent; drained from `fetchRange`, so the
   * drain runs after the fetcher removed the completed entry and never while
   * another covering request could swallow the retry.
   */
  #pendingRetry: Option<DateTime.Range<Utc>> = null;
  #retryInFlight = false;

  /** Fires when cell data changes for any column; `null` until the first change. */
  readonly dataInvalidated = new LiveData<Option<NaiveDate.Partial>>(null);

  /** Fires when a row's sync state transitions. */
  readonly onSyncStateChange = new LiveData<Option<RowLocalId>>(null);

  readonly #durable: Option<DurableHandoffs>;
  readonly #setCoordinator: CellSetCoordinator;
  readonly #durableCellRoute: "backend" | "api";
  readonly #api: TypedApi<Network.Routes>;
  readonly #networkKey: SheetNetworkKey;
  /** Where what this accessor's change and sync-state listeners threw is
   *  reported, out of band (`#notifyChange`, `#fireSyncStateChanges`). */
  readonly #listenerErrors: ListenerErrorSink;
  readonly #page: PageLifecycle;
  constructor(
    networkKey: SheetNetworkKey,
    /** The typed API this accessor persists rows through. */
    api: TypedApi<Network.Routes>,
    backend: CellCachingBackend,
    /**
     * The sheets-side `Backend` beside `backend` (`SheetHandleDeps.sheets`),
     * which serves a row's cells by network key whatever its local id.
     */
    sheets: SheetsBackend,
    ensurePromoted: Option<() => Promise<void>>,
    invalidation: Option<InvalidationSink>,
    /**
     * The owning client's durable writes in flight
     * (`SheetHandleDeps.durableWrites`), through which a refusal of a
     * `setDurable` write or a row's `updateDurable` reaches it; `null` for an
     * accessor no client routes refusals to (its durable writes settle once
     * handed off).
     */
    durable: Option<DurableHandoffs>,
    setCoordinator: CellSetCoordinator = new CellSetCoordinator(),
    durableCellRoute: "backend" | "api" = "backend",
    /**
     * The host's sink for what a change or sync-state listener threw
     * (`ListenerErrors.RETHROW`: the host's uncaught error, next microtask).
     * A listener is never part of the fetch or write that notified it.
     */
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
    /**
     * The owning client's page lifecycle (`SheetHandleDeps.page`), which
     * tells a fetch aborted by its page's unload apart from a failure; a
     * page-less one (never leaving) for an accessor no client owns.
     */
    page: PageLifecycle = new PageLifecycle(null),
  ) {
    this.#networkKey = networkKey;
    this.#page = page;
    this.#listenerErrors = listenerErrors;
    this.#setCoordinator = setCoordinator;
    this.#durableCellRoute = durableCellRoute;
    this.#api = api;
    this.#durable = durable;
    this.#backend = backend;
    this.#sheets = sheets;
    this.#ensurePromoted = ensurePromoted ?? null;
    this.#invalidation = invalidation;
    this.#fetchPlan = new RowFetchPlan({
      sheets,
      invalidation,
      settleActor: CellAccessor.FETCH_SETTLE_ACTOR,
      ownerOf: (rowId) => this.#fetchOwner(rowId),
      childrenOf: (rowId) => (this.#resolvers?.childrenOf(rowId) ?? []).map((id) => RowNetworkKey.fromWire(id)),
      rowHandle: (rowId) => this.rowHandle(rowId),
      localIdsOf: (rowId) => {
        const interned = this.#ids?.get(RowKey.of(rowId));
        return this.#resolvers?.localIds(rowId) ?? (interned ? [interned] : []);
      },
    });
  }

  /**
   * @internal Bind the owning sheet's registries: the dependency graph that
   * drives formula re-evaluation, the client's `RowLocalId` / `SheetRow`
   * registries that resolve a row id to its access key and metadata, and the
   * sheet's rows-side-first `resolvers`. Called once by `SheetHandle.parse`
   * (client.ts); until then the accessor keys handles by the raw row id and
   * skips formula dependents.
   */
  bindRegistries(
    graph: Option<DependencyGraph<RowLocalId>>,
    ids: RowLocalId.Registry,
    rows: Option<SheetRow.Registry>,
    resolvers: Option<CellAccessor.Resolvers>,
  ): void {
    this.#graph = graph;
    this.#ids = ids;
    this.#rows = rows;
    this.#resolvers = resolvers;
  }

  /**
   * Observe accepted cells. A store-writing observer can capture its own
   * response-time freshness boundary; that observation receives this response
   * instead of the fallback callback and releases on install, bail or failure.
   */
  setBulkFetchObserver(
    observer: Option<CellAccessor.BulkFetchObserver>,
    capture: Option<(range: Option<NaiveDate.Range>) => CellAccessor.BulkFetchObservation>,
  ): void {
    this.#bulkFetchObserver = observer;
    this.#captureBulkFetchObserver = capture;
    for (const row of this.#rowHandles.values()) row.fetchBindings.setInstallsAuthority(this, capture != null);
  }

  // ── RowCellCache access ──────────────────────────────────────────────────────

  /**
   * Resolve any row-id spelling to the row's shared cell-access key
   * (`cellAccessKey`): canonical (`:~`) lookup-keyed rows converge on
   * ONE handle whether addressed by UUID or by lookup key. Without
   * this, a UUID-keyed caller and a lookup-keyed caller split the
   * cache — the root of the stale-until-refetch bug class.
   */
  #accessKeyFor(rowId: RowNetworkKey): RowNetworkKey {
    const { key, localId, row } = this.#accessRouteFor(rowId);
    if (localId && row?.lookupKey) {
      // Converging this row onto its lookup-key access key — make the
      // registry resolve that key back to the row so later metadata
      // lookups (e.g. the per-row fetchFn, which receives the ACCESS
      // key) find it. Server rows get this alias in
      // `RowNode.decompose`; client-expanded virtual children (alias
      // template data-sources like `:~weather:{lid}`) don't pass
      // through decompose and would otherwise resolve no meta and
      // silently skip their per-row fetch.
      //
      // A canonical key can have several viewers (the `:calendar` header and
      // `:~lona:home` each expand their own default-weather child onto the
      // same `:~weather:{lid}`), and a promoted sheet's persisted alias row
      // carries the same key; the registry is client-scoped. The key keeps
      // one owner (a reader, not the alias wrapping it): see
      // `LookupKeyOwner`.
      if (this.#ids && this.#rows) {
        LookupKeyOwner.claim(
          this.#ids,
          this.#rows,
          row.lookupKey,
          localId,
          row.raw.type,
          "access",
        );
      }
    }
    return key;
  }

  /** Inspect the current route without claiming a lookup-key owner or creating a cache. */
  #accessRouteFor(rowId: RowNetworkKey) {
    const localId = this.#ids?.get(RowKey.of(rowId));
    const row = localId ? this.#rows?.get(localId) : null;
    const key = RowNetworkKey.fromWire(cellAccessKey(rowId, row?.lookupKey ?? null));
    return { key, localId, row };
  }

  /**
   * Where the cells of each row this accessor binds live: the view of its
   * sheet's one cell store (`CellStoreView`), `null` for a row whose cells
   * no store of the sheet holds. Set once by the owning handle; the rows
   * bound before are re-pointed.
   */
  #storeViews: Option<CellAccessor.StoreViews> = null;

  /** Native model recompute after an accepted write; null keeps legacy cache refresh. */
  #residentFormulaRecompute: Option<CellAccessor.ResidentFormulaRecompute> = null;

  /** Install the owning model's resident evaluator; no demand or coverage is completed here. */
  setResidentFormulaRecompute(recompute: CellAccessor.ResidentFormulaRecompute): void {
    this.#residentFormulaRecompute = recompute;
  }

  /** Install the store-view resolver (see `#storeViews`); every row bound so far is re-pointed. */
  setStoreViews(resolve: CellAccessor.StoreViews): void {
    this.#storeViews = resolve;
    for (const [key, rh] of this.#rowHandles) {
      rh.fetchBindings.setView(this, resolve(key, this.#ids?.get(RowKey.of(key)) ?? null, rh));
    }
  }

  /** Get an existing RowCellCache without creating one. */
  peekRowHandle(rowId: RowNetworkKey): Option<RowCellCache> {
    return this.#rowHandles.get(this.#accessKeyFor(rowId)) ?? null;
  }

  /** The cache this accessor bound for the row `localId` names, without creating one. */
  peekRowHandleOf(localId: RowLocalId): Option<RowCellCache> {
    const key = this.#handleKeyByLocalId.get(localId);
    return key == null ? null : this.#rowHandles.get(RowNetworkKey.fromWire(key)) ?? null;
  }

  #sharedRowCache: Option<(id: RowId) => RowCellCache> = null;
  /** SDK composition keeps ordinary identities client-scoped across seeded sheet transports. */
  setRowCacheResolver(resolve: (id: RowId) => RowCellCache): void { this.#sharedRowCache = resolve; }

  /** Resolve facts independently of the sheet transport that fetches them. */
  #resolveRowCache(id: RowId): RowCellCache {
    return this.#sharedRowCache?.(id) ?? this.#backend.rowHandle(id);
  }

  rowHandle(rawRowId: RowNetworkKey): RowCellCache {
    const rowId = this.#accessKeyFor(rawRowId);
    let rh = this.#rowHandles.get(rowId);
    if (rh) return rh;

    // COW strand witness: a fresh empty handle is about to be created for
    // `rowId`. If this row's STABLE identity already has a live,
    // coverage-bearing handle under a DIFFERENT key, that populated handle is
    // being stranded — an ephemeral→server promote re-keyed the row's wire id
    // (which IS the access key for sheet-local rows) but not this registry, so
    // every non-focused cell blanks to a loading shimmer then refills (the
    // whole-sheet COW flash). `rekeyHandles` migrates the populated handle to
    // the server key so this path HITs above and never reaches here; a fire is
    // the regression. Coverage-based, so it catches empty strands too.
    const localId = this.#ids?.get(RowKey.of(rowId)) ?? null;
    if (localId) {
      const priorKey = this.#handleKeyByLocalId.get(localId);
      if (priorKey != null && priorKey !== rowId) {
        const priorRh = this.#rowHandles.get(RowNetworkKey.fromWire(priorKey));
        if (
          priorRh != null &&
          (priorRh.fetcher.fetchedRange != null ||
            priorRh.deliveredRange != null ||
            priorRh.allCells().some((c) => c.kind === "date"))
        ) {
          Tracing.log("cow-strand-blank", {
            serial: localId.serial,
            oldKey: priorKey,
            newKey: rowId,
          });
        }
      }
    }

    rh = this.#resolveRowCache(RowId.of(rowId));
    // Adopting retained cells can synchronously announce back into this accessor.
    // Register first so that re-entry finds this cache and cannot adopt recursively.
    const priorLocalKey = localId == null ? null : this.#handleKeyByLocalId.get(localId);
    this.#rowHandles.set(rowId, rh);
    if (localId) this.#handleKeyByLocalId.set(localId, rowId);
    try {
      const view = this.#storeViews?.(RowNetworkKey.fromWire(rowId), localId, rh) ?? null;
      if (view?.sheetScoped) {
        rh = rh.fork();
        this.#rowHandles.set(rowId, rh);
        this.#contextRows.add(rh);
      }
      rh.fetchBindings.bind({
        owner: this,
        fetchFn: this.#ingestingFetchFn(RowNetworkKey.fromWire(rowId), rh),
        sheetFetch: (range) => this.demandFetch(range),
        invalidateSheetCache: () => { this.#sheetFetcher.fetchedRange = null; },
        view,
        installsAuthority: this.#captureBulkFetchObserver != null,
      });

      // Virtual rows (from ChildSpec) bypass the server, so their metadata
      // is never in backend.rowMetaCache. Store it here so index factories
      // (e.g., EventsIndex) are applied to their RowHandles.
      const meta = localId ? this.#rows?.get(localId) : null;
      if (meta) {
        // The resolved cache may belong to the client while this backend is
        // a seeded sheet transport. Both need the metadata they consume.
        const preparedMeta = this.#rows?.effectiveMetadata(meta.raw, rowId) ?? meta.raw;
        const typeCardinality = CellAccessor.#typeCardinality(preparedMeta.type);
        rh.applyMeta(preparedMeta, typeCardinality);
        if (!this.#contextRows.has(rh)) this.#backend.storeRowMeta(
          RowId.of(rowId),
          SheetRowMeta.of(preparedMeta, SheetRowMeta.DETACHED),
          typeCardinality,
        );
        const descriptor = meta.descriptor;
        if (AliasResolver.is(descriptor.dataOwner) && !rh.encoding) {
          this.#inheritAliasEncoding(rh, meta, descriptor.dataOwner.batchKey(meta));
        }
        // Cell hydration runs at the framework layer now via the
        // encoding registry + `row.metadata.encoding`. Legacy
        // descriptor `cellParser` validators are dead code; see
        // RowCellCache.parseCellData.

        if (!rh.computeRange) {
          if (CellProvider.is(descriptor.dataOwner)) {
            const provider = descriptor.dataOwner;
            rh.setComputeRange((naiveRange) =>
              Promise.resolve(provider.computeCells(naiveRange, meta)),
            );
            rh.kind = "formula";
          }
        }
      }

      // Renderer-virtual rows (`virtual(r_<hex>|renderer)`) project from a
      // source row — wire `sourceHandle + kind="series"` so cell reads
      // delegate to the source's `_fetched`, picking up local writes
      // (and their actor stamps) from the bridge in `client.ts`. Forward
      // the source's listener fires to this handle so subscriptions on
      // the renderer-virtual row's `CellHandle.onData` re-evaluate when
      // the source updates.
      const parsedId = RowId.of(rowId);
      const virt = parsedId.asVirtual();
      if (virt && virt.preset === "renderer" && !rh.sourceHandle) {
        const sourceRh = this.#resolveRowCache(virt.parentId);
        rh.bindSourceHandle(sourceRh);
      }

      // A handle born while a bulk response is parked is a target that response
      // may install into: every open lease adopts it now, so an edit made
      // through it before the release is protected like any other.
      for (const lease of this.#leases) lease.adopt(RowNetworkKey.fromWire(rowId), rh);
      return rh;
    } catch (error) {
      // A failed view adapter must leave neither a phantom registry entry nor a hold.
      rh.fetchBindings.unbind(this);
      if (this.#contextRows.delete(rh)) {
        rh.computeRange = null;
        rh.disposeAll();
      }
      this.#rowHandles.delete(rowId);
      if (localId) {
        if (priorLocalKey != null) this.#handleKeyByLocalId.set(localId, priorLocalKey);
        else this.#handleKeyByLocalId.delete(localId);
      }
      throw error;
    }
  }

  /**
   * A parked response could not be installed into `rows` (their handles were
   * replaced outside the lease's view). The fetcher already counts the range
   * as covered, so a same-range demand would be a cache hit forever: drop
   * that coverage and fetch the range again once this fetch's entry is gone,
   * so the retry is a real request, installed under a lease that owns the
   * current handles. Observable as `sheet/ingestion/retry`.
   */
  #retryAfterUnownedInstall(utcRange: DateTime.Range<Utc>, rows: ReadonlySet<string>): void {
    Tracing.log("sheet/ingestion/retry", { sheetId: this.#networkKey, rows: [...rows] });
    const p = this.#pendingRetry;
    this.#pendingRetry = p == null ? utcRange : new DateTime.Range(
      p.start.mse < utcRange.start.mse ? p.start : utcRange.start,
      p.end.mse > utcRange.end.mse ? p.end : utcRange.end,
    );
  }

  /**
   * Every sheet-level fetch goes through here so the retry intent is drained
   * exactly when a fetch has completed and the fetcher is idle: at that point
   * the completed entry is gone and no covering in-flight request exists, so
   * the retry is a real request rather than a coalesced no-op.
   */
  #fetchRange(range: DateTime.Range<Utc>): Promise<Option<DateTime.Range<Utc>>> {
    return this.#sheetFetcher.fetchRange(range).then((r) => r ?? null).finally(() => this.#drainRetry());
  }

  #drainRetry(): void {
    if (this.#disposed || this.#retryInFlight || this.#sheetFetcher.hasOngoingFetches) return;
    const range = this.#pendingRetry;
    if (range == null) return;
    this.#pendingRetry = null;
    this.#retryInFlight = true;
    // `fetchedRange` is the fetcher's public coverage field (there is no reset
    // method despite old comments); nothing is in flight here, so this drops
    // exactly the coverage the skipped response claimed.
    this.#sheetFetcher.fetchedRange = null;
    this.#fetchRange(range)
      .catch((err) => Tracing.err("net")("ingestion retry failed", { error: String(err) }))
      .finally(() => {
        this.#retryInFlight = false;
        this.#drainRetry();
      });
  }

  /** One re-key of {@link rekeyHandles}. */
  #rekeyOneHandle(fromRaw: string, toRaw: string): void {
    if (fromRaw === toRaw) return;
    const rh = this.#rowHandles.get(RowNetworkKey.fromWire(fromRaw));
    if (rh == null) return; // canonical row (keyed by stable lookupKey) or unseen
    // The migration runs before any post-promote re-resolve, so a fresh empty
    // handle should not exist under the server key yet — but if one does, drop
    // it in favor of the populated handle.
    const stale = this.#rowHandles.get(RowNetworkKey.fromWire(toRaw));
    if (stale != null && stale !== rh) stale.disposeAll();
    this.#rowHandles.delete(RowNetworkKey.fromWire(fromRaw));
    this.#rowHandles.set(RowNetworkKey.fromWire(toRaw), rh);
    for (const lease of this.#leases) lease.rekey(fromRaw, toRaw, rh);
    // Re-point routing (meta / lookup / canonical fetch branches key off the
    // id at call time) to the server id so future fetches target the persisted
    // row.
    rh.fetchBindings.bind({
      owner: this,
      fetchFn: this.#ingestingFetchFn(RowNetworkKey.fromWire(toRaw), rh),
      sheetFetch: (range) => this.demandFetch(range),
      invalidateSheetCache: () => { this.#sheetFetcher.fetchedRange = null; },
      view: this.#storeViews?.(RowNetworkKey.fromWire(toRaw), this.#ids?.get(RowKey.of(toRaw)) ?? null, rh) ?? null,
      installsAuthority: this.#captureBulkFetchObserver != null,
    });
    // Keep the backend's handle registry unified on the same instance so a
    // standalone `_ensureRowHandle` reader converges on it under the server id.
    this.#backend.rekeyRowHandle(RowId.of(fromRaw), RowId.of(toRaw));
    // Keep the strand-witness index current (else a later miss would falsely
    // fire `cow-strand-blank` for this already-migrated row).
    const lid = this.#ids?.get(RowKey.of(toRaw)) ?? null;
    if (lid) this.#handleKeyByLocalId.set(lid, toRaw);
  }

  /**
   * Promote re-key: an ephemeral→server promote mutates each row's wire id IN
   * PLACE (`RowLocalId.Registry.rekey`), and for sheet-local rows the wire id IS the
   * cell-access key (`cellAccessKey`). Without this, the populated `RowCellCache`
   * (seed + just-typed cells, full coverage) is stranded under the OLD ephemeral
   * key while the post-promote re-resolve builds a FRESH EMPTY handle under the
   * server key → every non-focused cell blanks then refills (the whole-sheet COW
   * flash). Mirror `SheetModelCache.rekeyRows`: move each handle to its server key
   * IN PLACE (same instance, cells + coverage intact) so later
   * `rowHandle(serverId)` reads HIT the populated handle and never render empty.
   * Canonical/lookup-keyed rows are immune (their access key is the stable
   * lookupKey). MUST be called synchronously from `SheetHandle._applyPromotion`,
   * before any async re-render, so the migration always precedes the re-resolve.
   */
  rekeyHandles(idRemap: ReadonlyMap<string, string>): void {
    for (const [fromRaw, toRaw] of idRemap) {
      this.#rekeyOneHandle(fromRaw, toRaw);
    }
  }

  /**
   * Renderer rows are visual/editing projections over their parent source row.
   * Keep a renderer-scoped handle for identity, but delegate reads and listener
   * fires to the source handle so local source writes repaint renderer cells.
   */
  bindRendererToSource(rendererRowId: RowNetworkKey, sourceRowId: RowNetworkKey): void {
    const rendererRh = this.rowHandle(rendererRowId);
    const sourceRh = this.rowHandle(sourceRowId);
    rendererRh.bindSourceHandle(sourceRh);
  }

  /**
   * Build a per-row fetch function that consults the RowLocalId registry
   * and row type at call time. This handles:
   * - Virtual rows promoted to UUID via registry alias
   * - Rows with lookup keys (fetch by key instead of ID)
   * - Layout rows that never need per-row fetch
   */
  /**
   * The row's per-row fetch, its answer installed into the sheet's one cell
   * store the way a bulk answer is (the bulk observer: decoded by the sheet's
   * rows, a cell edited after the request went out left as it is) before the
   * row's cache sees it.
   */
  #ingestingFetchFn(
    rowId: RowNetworkKey,
    rh: RowCellCache,
  ): RowCellCache["fetchFn"] {
    const fetchFn = this.#makeFetchFn(rowId, rh);
    return async (range: DateTime.Range<Utc>, complete: Option<RowFetchCompletion> = null) => {
      // A row's own read, not a sheet-wide one: no range for the rows' loads to wait on.
      const observation = this.#observe(null);
      let failed = false;
      try {
        const cells = await fetchFn(range);
        const install = (): void => {
          if (observation != null && cells.length > 0 && !this.#disposed) {
            const localId = this.#ids?.get(RowKey.of(rowId)) ?? null;
            const meta = localId == null ? null : this.#rows?.get(localId) ?? null;
            try {
              observation.receive({
                networkKey: this.#networkKey,
                rowId,
                serverRowId: RowId.of(rowId),
                lookupKey: meta?.lookupKey ?? null,
                rowType: meta?.raw.type ?? null,
                label: meta?.raw.label ?? null,
                metadata: null,
                utcRange: range,
                cells: rh.decodeCells(cells),
              });
            } catch (err) {
              // The handle's rows-side sheet moved on (a promote, a close): the
              // row's cache still takes the answer; the store is another's now.
              Tracing.log("sheet/row-fetch-ingest-skipped", { rowId, err: err instanceof Error ? err.message : String(err) });
            }
          }
        };
        // Only the synchronous response commit is deferred; HTTP never holds a bracket.
        // Keep completion outside the ingest-only catch so its failures propagate.
        if (complete == null) install();
        else complete(cells, install);
        return cells;
      } catch (error) {
        failed = true;
        throw error;
      } finally {
        try { observation?.release(); }
        catch (error) { if (!failed) throw error; }
      }
    };
  }

  #makeFetchFn(
    rowId: RowNetworkKey,
    rh: RowCellCache,
  ): (range: DateTime.Range<Utc>) => Promise<Network.SheetCell[]> {
    return this.#fetchPlan.fetchFn(rowId, rh);
  }

  /**
   * The fetch plan's view of the row's data owner (`RowFetchPlan.Owner`), read
   * from the row's descriptor at fetch time; `null` when the sheet has no
   * metadata for the row.
   */
  #fetchOwner(rowId: RowNetworkKey): Option<RowFetchPlan.Owner> {
    const localId = this.#ids?.get(RowKey.of(rowId));
    const meta = localId ? this.#rows?.get(localId) : null;
    if (!meta) return null;
    const owner = meta.descriptor.dataOwner;
    if (owner === "layout") return { kind: "layout" };
    if (CellProvider.is(owner)) {
      return { kind: "provider", computeCells: (range) => owner.computeCells(range, meta) };
    }
    if (AliasResolver.is(owner)) {
      const batchKey = owner.batchKey(meta);
      return { kind: "alias", batchKey, ensureEncoding: (rh) => this.#ensureAliasEncoding(rh, meta, batchKey) };
    }
    return { kind: "plain", lookupKey: meta.lookupKey ?? null };
  }

  #inheritAliasEncoding(
    rh: RowCellCache,
    row: SheetRow,
    batchKey: string,
  ): void {
    rh.sourceHandle ??= this.#backend.rowHandle(RowId.of(batchKey));
    if (rh.encoding) return;
    const sourceMeta = this.#backend.rowMeta(RowId.of(batchKey));
    if (!sourceMeta) return;
    const encoding = this.#encodingFromMetadata(sourceMeta.metadata);
    if (!encoding) return;

    const rowMeta = row.raw;
    const metadata = this.#mergeMetadataEncoding(rowMeta.metadata, encoding);
    this.#backend.storeRowMeta(
      RowId.of(row.id.toString()),
      SheetRowMeta.of({ ...rowMeta, metadata }, SheetRowMeta.DETACHED),
      CellAccessor.#typeCardinality(rowMeta.type),
    );
  }

  async #ensureAliasEncoding(
    rh: RowCellCache,
    row: SheetRow,
    batchKey: string,
  ): Promise<void> {
    this.#inheritAliasEncoding(rh, row, batchKey);
    if (rh.encoding) return;

    const rowKey = RowNetworkKey.forRead(RowNetworkKey.fromWire(batchKey));
    // Checked: only a 404 means "no source row, keep the default encoding";
    // any other failure fails the fetch instead of decoding with a guess.
    const sourceRow = await SdkRequestError.checkRequest(
      "rows",
      "get",
      "GET",
      "/api/rows/{rowKey}",
      () =>
        SdkRequestError.notFoundAsNull(() =>
          this.#api.getResult("/api/rows/{rowKey}", { path: { rowKey } })
        ),
    );
    if (!sourceRow) return;
    this.#backend.storeRowMeta(
      RowId.of(batchKey),
      SheetRowMeta.of(sourceRow, SheetRowMeta.DETACHED),
      CellAccessor.#typeCardinality(sourceRow.type),
    );
    this.#inheritAliasEncoding(rh, row, batchKey);
  }

  #encodingFromMetadata(metadata: Option<Json>): Option<string> {
    return Json.string(Json.object(metadata), "encoding");
  }

  #mergeMetadataEncoding(
    metadata: Option<Json>,
    encoding: string,
  ): JsonObject {
    return { ...(Json.object(metadata) ?? {}), encoding };
  }

  #toUtcRange(range: NaiveDate.Range): DateTime.Range<Utc> {
    return new DateTime.Range(
      DateTime.fromDse(range.start.dse, Utc),
      DateTime.fromDse(range.end.dse, Utc),
    );
  }

  /**
   * Start a bulk fetch for the rendered range. Called by the Sheet in
   * onRenderedColumnsWillChange, before onBind fires for columns.
   * RangeBasedFetcher handles padding, caching, and deduplication.
   */
  /** Install the response-install gate; replacing one wakes its waiters. */
  setIngestionGate(gate: IngestionGate): void {
    this.#ingestion.set(gate);
  }

  /** Owner-scoped removal: clears only while `owner` is the installed gate. */
  clearIngestionGate(owner: IngestionGate): void {
    this.#ingestion.clear(owner);
  }

  /** Replacing a renderer's pacer releases its queued installs. */
  setInstallPacer(pacer: InstallPacer): void {
    const previous = this.#installPacer;
    if (previous === pacer) return;
    this.#installPacer = pacer;
    if (previous != null) previous.release(this);
  }

  /** Clear only the installed owner; queued installs are never dropped. */
  clearInstallPacer(owner: InstallPacer): void {
    if (this.#installPacer !== owner) return;
    owner.release(this);
    this.#installPacer = null;
  }

  /**
   * The accessor's ingestion gate slot, exposed for the SDK's
   * instrumentation seam (`instrumentationOf(handle)`); read-only handle,
   * do not wire from it.
   * @internal
   */
  get ingestionSlot(): IngestionGate.Slot {
    return this.#ingestion;
  }

  /**
   * Every live row handle, for the SDK's instrumentation seam
   * (`instrumentationOf(handle)`). Iteration only.
   * @internal
   */
  instrumentationRowHandles(): Iterable<RowCellCache> {
    return this.#rowHandles.values();
  }

  /**
   * The accessor's white-box seam for the SDK's own tests (see
   * `CellAccessor.Instrumentation`). Patching through it changes how the
   * accessor behaves; application code never calls it.
   * @internal
   */
  instrumentation(): CellAccessor.Instrumentation {
    return {
      swapRowHandle: (key, rh) => {
        this.#rowHandles.set(RowNetworkKey.fromWire(key), rh);
      },
    };
  }

  startFetch(range: NaiveDate.Range): void {
    // Non-fatal: a rejected bulk-range fetch silently yields blank cells, so
    // surface it in the console rather than swallowing — unless the browser
    // aborted it because this page is unloading (`PageLifecycle`).
    this.#fetchRange(this.#toUtcRange(range)).catch((err) =>
      this.#page.reportUnlessLeaving(err instanceof Error ? err : new Error(String(err)), () =>
        Tracing.err("net")("startFetch bulk range failed", {
          error: String(err),
        })),
    );
  }

  /**
   * Mark this accessor's sheet active/inactive. The Sheet UI calls this on bind:
   * the outgoing sheet is deactivated, the incoming one activated. Inactive
   * accessors bail their in-flight fetch continuations (the rapid-switch
   * avalanche fix, Migration P2). Reactivating a sheet whose fetch was dropped
   * while inactive resets the optimistic coverage so it re-fetches — the
   * re-shown sheet is never left stale. Idempotent.
   */
  setActive(active: boolean): void {
    if (active && this.#bailedWhileInactive) {
      this.#bailedWhileInactive = false;
      // Drop the optimistic coverage the bailed fetch left behind so the
      // re-shown sheet re-fetches. `fetchedRange` is the fetcher's public
      // coverage field (there is no `resetFetchCache()` despite old comments).
      this.#sheetFetcher.fetchedRange = null;
    }
    this.#isActive = active;
  }

  /** @internal Mark a just-inserted empty row covered by delivered sheet data. */
  markInsertedRowKnownEmpty(rowId: RowNetworkKey): void {
    const fetchedRange = this.#sheetFetcher.installedRange;
    if (!fetchedRange) return;
    this.rowHandle(rowId).markRangeFetched(fetchedRange);
  }

  /**
   * Await a bulk fetch that covers the given range. Called by
   * RowCellCache.sheetFetch. Resolves immediately if already covered.
   */
  demandFetch(range: NaiveDate.Range): Promise<void> {
    return this.#fetchRange(this.#toUtcRange(range)).then(() => {});
  }

  // ── Single cell access ────────────────────────────────────────────────────

  /**
   * Get a cached cell. Returns null if not in cache.
   */
  peek(rowId: RowNetworkKey, date: string): Option<CellEntry> {
    const rh = this.#rowHandles.get(rowId);
    if (!rh) return null;
    const naiveDate = NaiveDate.parse(date);
    const range = new NaiveDate.Range(naiveDate, naiveDate.addDays(1));
    const cells = TaggedCells.dates(rh.cellsIn(range));
    return cells[0] ?? null;
  }

  /**
   * Get a cell (from cache or single-row fetch).
   */
  async get(rowId: RowNetworkKey, date: string): Promise<Option<CellEntry>> {
    const cached = this.peek(rowId, date);
    if (cached) return cached;

    const start = NaiveDate.parse(date);
    const end = start.addDays(1);
    const rh = this.rowHandle(rowId);
    const utcRange = new DateTime.Range(
      DateTime.fromDse(start.dse, Utc),
      DateTime.fromDse(end.dse, Utc),
    );
    await rh.fetch(utcRange);
    const range = new NaiveDate.Range(start, end);
    const cells = TaggedCells.dates(rh.cellsIn(range));
    return cells[0] ?? null;
  }

  /**
   * The ONE precondition of every cell write: await the sheet's COW promote
   * (an ephemeral sheet's first write claims it on the server), then answer
   * the row's CURRENT wire key, resolved from its stable local identity —
   * a promote re-keys the row, so a key captured before the await names a
   * row the server does not have. Every writer (`set`, `setDurable`, `add`,
   * `setCustom`, `deleteCustom`, `generate`, and a joined row's timepoint
   * verbs through `JoinedCellWriter.promotedRow`) starts here and uses only
   * the key it answers; the hygiene test `promote-rekey` fails a writer
   * that awaits the promote itself or uses its pre-await key after it.
   */
  async promotedRow(rowId: RowNetworkKey): Promise<RowNetworkKey> {
    const localId = this.#ids?.get(RowKey.of(rowId)) ?? null;
    await this.#ensurePromoted?.();
    return localId == null ? rowId : RowNetworkKey.of(localId);
  }

  /**
   * The key of one cell's write queue (`CellSetCoordinator`): the client's
   * user, the row — canonical lookup rows by their shared access key (they
   * can have several local viewers), other rows by their process-stable
   * local identity so a COW promotion cannot split one cell's queue by
   * re-keying its wire id — and the cell's date or id.
   */
  #cellQueueKey(rowId: RowNetworkKey, date: NaiveDate.Partial, id: Option<string>): string {
    const localId = this.#ids?.get(RowKey.of(rowId)) ?? null;
    const accessKey = this.#accessKeyFor(rowId);
    const stableRow = isCanonicalKey(accessKey)
      ? `key:${accessKey}`
      : localId == null ? `row:${rowId}` : `local:${localId.serial}`;
    return JSON.stringify([
      this.#ids?.currentUser?.toString() ?? null,
      stableRow,
      id == null ? `date:${date.toString()}` : `id:${id}`,
    ]);
  }

  /**
   * Write a cell — the one writer behind a joined row's writable facade's `write`:
   * COW-promote the sheet, persist a lookup-seeded row, write the cell,
   * mirror it into the row's handle, and re-evaluate formula dependents.
   *
   * `id` is the stable cell id to upsert by `(row_id, id)`, or `null` for a
   * date-keyed write; `""` is rejected (it is not "no id"). Every write —
   * id-keyed or date-keyed — is checked through the same
   * `CellAccessor.persistCell` as a held row's facade `write`: a rejected or
   * unanswered write throws `CellWriteError` and nothing is mirrored.
   * `setDurable` is the service-worker-debounced alternative. The mirrored
   * cell records `actor`, the writer (`CellHandle.onData`'s `change.actor`).
   * An id-less write on a multi-cardinality row names no cell: it rejects
   * with `CellWriteError` (outcome `unaddressable`) before anything is done
   * (`add` adds a cell there).
   */
  async set(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void> {
    CellAccessor.requireCellId("CellAccessor.set", id);
    CellAccessor.checkAddress(
      this.rowHandle(rowId).cardinality,
      "date",
      id,
      data == null ? "clear-cell" : "write-cell",
      "CellAccessor.set",
    );
    // The editor can issue every keystroke before any POST settles. Serialize
    // writes to this cell, including their local mirrors, so a slow prefix
    // cannot overwrite a later complete value on the server or in the cache.
    // The client-owned coordinator scopes the queue across sheet accessors
    // (`#cellQueueKey`), and orders these writes' mirrors with the cell's
    // durable writes (`setDurable`) by when each reached the wire.
    const queueKey = this.#cellQueueKey(rowId, date, id);
    // Issue order with the cell's durable writes (`setDurable`): taken now,
    // before anything is awaited.
    const ticket = this.#setCoordinator.issue(queueKey, "checked");
    try {
      return await this.#setCoordinator.serialize(queueKey, this, async () => {
        // COW: promotion can re-key the row while awaited. Address the current
        // row and its canonical cell store, not the pre-promotion wire id.
        const currentRowId = await this.promotedRow(rowId);
        await this.ensurePersisted(currentRowId, position);
        const cellKey = this.#accessKeyFor(currentRowId);
        const send = this.#setCoordinator.open(queueKey);
        try {
          // A durable write of this cell issued before this one that the
          // executor has not sent yet would reach the server after it: wait
          // for it, so the server and the row both end on the write issued
          // last. (A durable issued after this one waits for this send.)
          await this.#setCoordinator.olderSent(ticket);
          try {
            this.#setCoordinator.stamp(send);
            this.#setCoordinator.done(ticket);
            await CellAccessor.persistCell(this.#api, cellKey, date, data, id);
          } catch (err) {
            this.#afterFailedWrite(err, currentRowId, data == null ? "cells.clear" : "cells.set");
            throw err;
          }
          // A write of this cell sent after this one is shown already.
          if (!this.#setCoordinator.mayShow(send)) return;
          await this.#applyWrittenCell(currentRowId, date, data, id, actor);
        } finally {
          this.#setCoordinator.close(send);
        }
      }, ticket);
    } finally {
      this.#setCoordinator.done(ticket);
    }
  }

  /**
   * A failed cell write's recovery (`afterFailedWrite`): when it may have
   * stored, the row is marked stale — its next read re-reads it — and the
   * change listeners told (`invalidateRow`). Nothing once the accessor was
   * disposed.
   */
  #afterFailedWrite(err: unknown, rowId: RowNetworkKey, what: string): void {
    afterFailedWrite(err, this.#owner, what, () => {
      this.invalidateRow(rowId);
      return undefined;
    });
  }

  /**
   * Add a date-keyed cell in column `date` of a multi-cardinality row — the
   * writer behind `CellTarget.add` on a row of a sheet. Same promote /
   * persist-row steps as `set`; the write is sent without an id (the server
   * mints it) and the server's answer is reconciled into the row's handle
   * (`reconcileAnswer`), then formula dependents re-evaluated. A
   * single-cardinality row rejects it with `CellWriteError` (outcome
   * `unaddressable`) before anything is done; a refused or unanswered write
   * throws `CellWriteError("write-cell")` and nothing is reconciled.
   */
  async add(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void> {
    CellAccessor.checkAddress(this.rowHandle(rowId).cardinality, "add", null, "write-cell", "CellAccessor.add");
    const currentRowId = await this.promotedRow(rowId);
    await this.ensurePersisted(currentRowId, position);
    let answer: Network.CellWriteResponse;
    try {
      answer = await CellAccessor.persistCell(this.#api, currentRowId, date, data, null);
    } catch (err) {
      this.#afterFailedWrite(err, currentRowId, "cells.add");
      throw err;
    }
    CellAccessor.reconcileAnswer(this.rowHandle(currentRowId), null, answer.cells, actor);
    await this.#afterWrite(currentRowId, date, null);
  }

  /**
   * Write a cell through the client's debounce executor — the writer behind
   * a joined row's facade `writeDurable` (whose `settled` this is). Same
   * promote / persist-row / mirror (recording `actor`) /
   * re-evaluate steps as `set`, but the wire write is handed to the
   * executor (the service worker owns it across reloads and coalesces rapid
   * edits). Resolves once the execution that carried the write finished,
   * and rejects with `CellWriteError` — nothing mirrored — when a refusal
   * of it reached the client's executor binding
   * (`ClientConfig.debounceExecutor`) first: step `write-cell` (`clear-cell` for an empty
   * value). An id-keyed write uses the API. A date-keyed write uses the
   * sheet's `Backend` except on a promoted ephemeral sheet, whose seeded
   * backend remains memory-only; that write uses the API too. A standalone
   * in-memory backend keeps date writes local. Each remote write is claimed
   * by its own request id: a refusal reaches
   * only the writes coalesced into the refused execution.
   */
  async setDurable(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void> {
    CellAccessor.requireCellId("CellAccessor.setDurable", id);
    CellAccessor.checkAddress(
      this.rowHandle(rowId).cardinality,
      "date",
      id,
      data == null ? "clear-cell" : "write-cell",
      "CellAccessor.setDurable",
    );
    // One queue key with the cell's checked writes (`set`): their mirrors
    // are ordered by when each reached the wire, whatever order the answers
    // arrive in (`CellSetCoordinator.mayShow`).
    const queueKey = this.#cellQueueKey(rowId, date, id);
    // Issue order with the cell's checked writes (`set`): taken now, before
    // anything is awaited; done once the handoff settled.
    const ticket = this.#setCoordinator.issue(queueKey, "durable");
    const send = this.#setCoordinator.open(queueKey);
    try {
      // Promotion rekeys the row's wire id: address the current row and its
      // canonical cell address (`promotedRow`).
      const currentRowId = await this.promotedRow(rowId);
      await this.ensurePersisted(currentRowId, position);
      const cellKey = this.#accessKeyFor(currentRowId);
      // A checked write of this cell issued before this one reaches the wire
      // first (it may still be waiting on the promote): the server and the
      // row then end on the write issued last.
      await this.#setCoordinator.olderSent(ticket);
      const handoff = id != null || this.#durableCellRoute === "api"
        ? this.#handOffDurable(cellKey, date, data, id, send)
        : this.#handOffDateKeyed(currentRowId, date, data, send);
      try {
        await handoff;
      } catch (err) {
        this.#afterFailedWrite(err, currentRowId, data == null ? "cells.clearDurable" : "cells.setDurable");
        throw err;
      }
      // A write of this cell sent after this one is shown already.
      if (!this.#setCoordinator.mayShow(send)) return;
      await this.#applyWrittenCell(currentRowId, date, data, id, actor);
    } finally {
      this.#setCoordinator.done(ticket);
      this.#setCoordinator.close(send);
    }
  }

  /**
   * Hand one row PATCH to the client's debounce executor — the writer
   * behind `SheetRow.Joined.Handle.updateDurable`. Resolves on the verdict
   * of the execution that carried it; rejects with `RowWriteError("update")`
   * when a refusal of it reached the client first.
   */
  patchRowDurable(
    rowId: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
  ): Promise<void> {
    return DurableHandoffs.persistRowPatch(this.#api, this.#durable, rowId, patch);
  }

  /**
   * Hand one date-keyed cell write to the sheet's `Backend` (whose HTTP
   * implementation debounces it under `durableCellKey(row, date, null)`);
   * throw its refusal. The write's own request id travels with it
   * (`SyncBackend.writeCell`'s `requestId`), so a refusal names the writes
   * coalesced into the execution that was refused and reaches only those —
   * a write to the same cell that was sent as its own request keeps its own
   * verdict. An in-memory backend keeps the write local,
   * ignores the id, and never refuses.
   */
  async #handOffDateKeyed(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    send: CellSetCoordinator.Send,
  ): Promise<void> {
    const durable = this.#durable;
    if (durable == null) {
      this.#setCoordinator.stamp(send);
      await this.#backend.writeCell(RowId.of(rowId), date, data, null, null);
      return;
    }
    const requestId = durable.nextRequestId();
    const failure = await durable.handOff(
      CellAccessor.durableCellKey(rowId, date, null),
      requestId,
      () => {
        this.#setCoordinator.stamp(send);
        return this.#backend.writeCell(RowId.of(rowId), date, data, null, requestId);
      },
    );
    if (failure == null) return;
    throw CellAccessor.durableRefusal(data == null ? "clear-cell" : "write-cell", failure);
  }

  /** Hand one API-addressed cell write to the executor; throw its refusal. */
  async #handOffDurable(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    send: CellSetCoordinator.Send,
  ): Promise<void> {
    const durable = this.#durable;
    if (durable == null) {
      this.#setCoordinator.stamp(send);
      await CellAccessor.persistCellDurable(this.#api, rowId, date, data, id, null);
      return;
    }
    const requestId = durable.nextRequestId();
    const failure = await durable.handOff(
      CellAccessor.durableCellKey(rowId, date, id),
      requestId,
      () => {
        this.#setCoordinator.stamp(send);
        return CellAccessor.persistCellDurable(this.#api, rowId, date, data, id, requestId);
      },
    );
    if (failure == null) return;
    throw CellAccessor.durableRefusal(data == null ? "clear-cell" : "write-cell", failure);
  }

  /** Mirror a written cell into its row handle (recording `actor`, the
   *  writer) and re-evaluate dependents. */
  async #applyWrittenCell(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    actor: Actor,
  ): Promise<void> {
    CellAccessor.mirrorWrite(this.rowHandle(rowId), date, data, id, actor);
    await this.#afterWrite(rowId, date, date.toRange());
  }

  /** After a cell write landed: re-evaluate the row's formula dependents
   *  and tell the change listeners. */
  async #afterWrite(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    residentRange: Option<NaiveDate.Range>,
  ): Promise<void> {
    // Legacy refresh and sheet notification retain the full fetched span.
    // Native set/clear recompute only written days; add's response collection
    // retains the full span because it can reconcile other dates.
    const staleIds = new Set<string>([rowId]);
    const fr = this.#sheetFetcher.fetchedRange;
    const written = residentRange ?? date.toRange();
    const range = fr
      ? new NaiveDate.Range(
        fr.start.dse < written.start.dse ? NaiveDate.fromDse(fr.start.dse) : written.start,
        fr.end.dse > written.end.dse ? NaiveDate.fromDse(fr.end.dse) : written.end,
      )
      : written;
    await this.#evaluateDependents(rowId, range, staleIds, residentRange);
    this.#notifyChange(staleIds, range, { kind: "accessor" });
  }

  /** Write a custom cell keyed by a DateTime range. `replaceCellId`
   *  requests the server's atomic move (delete old cell + insert).
   *  Checked: a server refusal throws `CellWriteError("write-timepoint-cell")`
   *  from the sheet's backend before anything is reconciled. */
  async setCustom(
    rowId: RowNetworkKey,
    dateRange: DateTime.Range<Utc>,
    data: Json | CellValue,
    replaceCellId: Option<CellId>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void> {
    // COW: promote an ephemeral sheet before its first custom-cell write,
    // and address the row as the promote re-keyed it.
    const currentRowId = await this.promotedRow(rowId);
    await this.ensurePersisted(currentRowId, position);
    const rh = this.rowHandle(currentRowId);
    let resp: Option<Network.CellWriteResponse>;
    try {
      resp = await this.#timepointBackend().writeTimepointCell(
        RowId.of(currentRowId), dateRange, data, replaceCellId, null,
      );
    } catch (err) {
      this.#afterFailedWrite(err, currentRowId, "cells.setCustom");
      throw err;
    }
    this.#reconcileCustomWrite(rh, currentRowId, resp, replaceCellId, actor);
  }

  /**
   * The backend a timepoint write goes through once the sheet is promoted:
   * the sheet's `Backend`, except on a promoted ephemeral sheet
   * (`durableCellRoute` `"api"`), whose seeded backend stays memory-only —
   * a write there would never reach the server, so it goes to the API.
   */
  #timepointBackend(): Pick<CellCachingBackend, "writeTimepointCell" | "clearTimepointCell"> {
    if (this.#durableCellRoute !== "api") return this.#backend;
    return {
      writeTimepointCell: (rowId, dateRange, data, replaceCellId, expectedEtag) =>
        postTimepointCell(this.#api, rowId, dateRange, data, replaceCellId, expectedEtag),
      clearTimepointCell: (rowId, cellId, expectedEtag) =>
        postTimepointClear(this.#api, rowId, cellId, expectedEtag),
    };
  }

  /** Delete a multi-cardinality (id-keyed) custom cell by cell id.
   *  Checked: a server refusal throws `CellWriteError("clear-timepoint-cell")`
   *  before anything is reconciled. */
  async deleteCustom(rowId: RowNetworkKey, cellId: CellId, actor: Actor): Promise<void> {
    // COW: promote an ephemeral sheet before mutating a custom cell, and
    // address the row as the promote re-keyed it.
    const currentRowId = await this.promotedRow(rowId);
    const rh = this.rowHandle(currentRowId);
    let resp: Option<Network.CellWriteResponse>;
    try {
      resp = await this.#timepointBackend().clearTimepointCell(
        RowId.of(currentRowId),
        cellId,
        null,
      );
    } catch (err) {
      this.#afterFailedWrite(err, currentRowId, "cells.deleteCustom");
      throw err;
    }
    this.#reconcileCustomWrite(rh, currentRowId, resp, cellId, actor);
  }

  /**
   * Server custom writes are targeted by raw cell id. Remove the requested
   * storage cell, then upsert any canonical cells the server returned.
   */
  #reconcileCustomWrite(
    rh: RowCellCache,
    rowId: RowNetworkKey,
    resp: Option<Network.CellWriteResponse>,
    removeCellId: Option<CellId>,
    actor: Actor,
  ): void {
    if (resp) {
      CellAccessor.reconcileAnswer(rh, removeCellId, resp.cells, actor);
    }
    this.#notifyChange([rowId], null, { kind: "accessor" });
  }

  /**
   * Ensure a lookup-key seeded row exists server-side before the first
   * mutation. Visible row IDs stay on the lookup key, so subsequent
   * row/cell routes continue to address `/api/rows/:lookupKey/...`.
   */
  async ensurePersisted(
    rowId: RowNetworkKey,
    position: Option<Rational>,
  ): Promise<void> {
    if (this.#ensuredLookupRows.has(rowId)) return;

    if (!SheetId.parse(this.#networkKey)) return;

    const parsedRowId = RowId.of(rowId);
    if (!parsedRowId.isLookup() && !parsedRowId.isReserved()) return;

    const localId = this.#ids?.get(RowKey.of(rowId));
    const row = localId ? this.#rows?.get(localId) : null;
    if (!row?.lookupKey) return;

    let inflight = this.#ensureLookupRowPromises.get(rowId);
    if (!inflight) {
      inflight = this.#ensurePersistedInner(rowId, row, position);
      this.#ensureLookupRowPromises.set(rowId, inflight);
    }

    try {
      await inflight;
    } finally {
      if (this.#ensureLookupRowPromises.get(rowId) === inflight) {
        this.#ensureLookupRowPromises.delete(rowId);
      }
    }
  }

  /**
   * Get all cached cells for a row — any spelling of its id (UUID or lookup
   * key) reads the one handle it shares (see `peekRowHandle`).
   */
  peekAll(rowId: RowNetworkKey): CellEntry[] {
    const rh = this.peekRowHandle(rowId);
    if (!rh) return [];
    return TaggedCells.dates(rh.allCells());
  }

  /**
   * Get all cached cells in a date range.
   */
  peekInRange(rowId: RowNetworkKey, range: NaiveDate.Range): CellEntry[] {
    const rh = this.peekRowHandle(rowId);
    if (!rh) return [];
    return rh.getAllInRange(range);
  }

  /**
   * Get all cached cells in a DateTime range (custom/sub-day precision).
   */
  peekTimepointsInRange(
    rowId: RowNetworkKey,
    range: DateTime.Range<Utc>,
  ): TimepointCellEntry[] {
    const rh = this.peekRowHandle(rowId);
    if (!rh) return [];
    return rh.getAllTimepointsInRange(range);
  }

  /**
   * Synchronously move a custom cell in a row handle cache. The server
   * write path reconciles later.
   */
  optimisticMoveTimepointCell(
    rowId: RowNetworkKey,
    oldRange: DateTime.Range<Utc>,
    newRange: DateTime.Range<Utc>,
    id: string,
    data: Json | CellValue,
  ): void {
    this.rowHandle(rowId).optimisticMoveTimepointCell(
      oldRange,
      newRange,
      CellId.of(id),
      data,
    );
  }

  /**
   * The sync state of a row, read through no sheet: a `RowSyncState.Detail`
   * copied from its row handle's state on demand (`null`: nothing syncing).
   */
  peekSyncState(rowId: RowNetworkKey): Option<RowSyncState.Detail> {
    const rh = this.#rowHandles.get(rowId);
    if (!rh?.syncState) return null;
    return RowSyncState.Detail.of(
      rh.syncState,
      RowId.of(rowId),
      null,
      rh.fetcher.installedRange ?? null,
    );
  }

  /**
   * Fetch cells for a single row in a date range.
   */
  async fetchRow(rowId: RowNetworkKey, range: NaiveDate.Range): Promise<CellEntry[]> {
    const rh = this.rowHandle(rowId);
    return rh.getCells(range);
  }

  /**
   * Generate a cell value server-side and apply to cache. Throws
   * `CellWriteError("generate-cell")` when the server refuses or does not
   * answer.
   */
  async generate(rowId: RowNetworkKey, date: string): Promise<Json> {
    const parsedDate = NaiveDate.Partial.parse(date).exp();
    // A generated value is a write: promote first, address the current row.
    const currentRowId = await this.promotedRow(rowId);
    let cell: Network.SheetCell;
    try {
      cell = await CellWriteError.check(
        "generate-cell",
        "POST",
        "/api/rows/{rowKey}/cells/generate",
        () =>
          this.#api.postResult("/api/rows/{rowKey}/cells/generate", {
            path: { rowKey: RowNetworkKey.fromWire(currentRowId) },
            body: { date: parsedDate.toString() },
          }),
      );
    } catch (err) {
      // The server may have stored the generated value.
      this.#afterFailedWrite(err, currentRowId, "cells.generate");
      throw err;
    }
    const rh = this.rowHandle(currentRowId);
    rh.applyExternalEdit(parsedDate, cell.data ?? null, null);
    return cell.data ?? null;
  }

  /**
   * Fire sync state change notifications for stale rows.
   * Called after fetch() resolves and listeners are notified. Each row's
   * notification runs even when an earlier one's listener threw; what they
   * threw is reported out of band, never into the fetch.
   */
  #fireSyncStateChanges(staleIds: Set<string>): void {
    const ids = this.#ids;
    if (!ids) return;
    const errors: Thrown[] = [];
    for (const rowIdStr of staleIds) {
      const rh = this.#rowHandles.get(RowNetworkKey.fromWire(rowIdStr));
      if (rh?.syncState) {
        const localId = ids.resolve(RowKey.of(rowIdStr));
        ListenerErrors.isolate(errors, () => this.onSyncStateChange.update(localId));
      }
    }
    ListenerErrors.report(this.#listenerErrors, errors, "CellAccessor");
  }

  // ── Staleness ─────────────────────────────────────────────────────────────

  /**
   * Check if a row's data is stale.
   */
  isStale(rowId: RowNetworkKey): boolean {
    return this.#rowHandles.get(rowId)?.isStale ?? false;
  }

  /**
   * Ensure a row's data is fresh (re-evaluate formulas or re-fetch).
   */
  async ensureFresh(rowId: RowNetworkKey, range: NaiveDate.Range): Promise<void> {
    const rh = this.#rowHandles.get(rowId);
    if (rh) await rh.ensureFresh(range);
  }

  // ── Bulk fetch ────────────────────────────────────────────────────────────

  /**
   * Bulk fetch cells for all rows in a date range.
   * Distributes to RowCellCache caches and notifies listeners.
   * Delegates to the sheet-level RangeBasedFetcher for padding,
   * range caching, and deduplication.
   */
  async fetch(range: NaiveDate.Range): Promise<void> {
    if (this.#disposed) return;
    await this.#fetchRange(this.#toUtcRange(range));
  }

  /**
   * Core bulk fetch implementation — the fetcher function for sheetFetcher.
   * Calls the bulk API, distributes to RowHandles, evaluates formulas.
   */
  async #bulkFetch(utcRange: DateTime.Range<Utc>): Promise<void> {
    if (this.#disposed) return;

    // The days the range touches (`UtcDays`, the cell reads' one
    // conversion); the fetcher's ranges are midnight-aligned, where that is
    // the plain day truncation.
    const naiveRange = UtcDays.touched(utcRange);

    const requestObservation = this.#observe(naiveRange);
    let results: Awaited<ReturnType<SheetsBackend["fetchSheetCells"]>>;
    try {
      results = await this.#sheets.fetchSheetCells(this.#networkKey, naiveRange);
    } catch (err) { requestObservation?.release(); throw err; }
    if (!results) { requestObservation?.release(); return; }

    // P2 superseded-sheet bail. If the sheet was switched away while this fetch
    // was in flight, skip the distribute → formula → listener cascade below —
    // run across every sheet a rapid switch abandons, that cascade is the
    // multi-second microtask avalanche. `bailedWhileInactive` makes
    // reactivation re-fetch so the optimistically-covered range is never left
    // unapplied. (`disposed` is the destruction case; `isActive` the switch case.)
    // Checked twice: before the gate wait, so a sheet already switched away
    // never parks behind a gesture, and after it, as of the release.
    const port = this.#bulkInstallPort();
    const bail = (): boolean => port.bail();
    if (bail()) { requestObservation?.release(); return; }
    // The response is in hand; the distribute → formula → listener cascade
    // below waits for the gesture, if any. Each handle's local-write revision
    // is captured first so a cell edited during the wait is never overwritten
    // by this older snapshot.
    // The lease covers the response's own targets — created now, so an edit
    // made through them during the wait is stamped against this snapshot —
    // and adopts any handle born later (see `rowHandle`).
    const lease = new CellAccessor.Lease();
    this.#leases.add(lease);
    const install = new BulkInstall(port, lease, requestObservation, utcRange, naiveRange);
    const { seen, staleIds, coveredIds, retryRows } = install;
    let observation: Option<CellAccessor.BulkFetchObservation> = null;
    let pace: Option<InstallPacer.Lease> = null;
    try {
      try {
        observation = requestObservation;
        // Acquisition runs inside the registry and the `finally`: a target that
        // fails to construct leaves no earlier target captured. Every handle
        // that exists now is captured too, not only the response's targets: a
        // promotion during the wait can make any of them the winner under a
        // target's key, and only a handle captured *now* can be installed into
        // with this snapshot's freshness boundary.
        for (const [rowId, rh] of this.#rowHandles) lease.adopt(rowId, rh);
        for (const rowData of results) {
          const rowId = this.#visibleRowIdForBulkResult(rowData);
          lease.adopt(RowNetworkKey.fromWire(rowId), this.rowHandle(RowNetworkKey.fromWire(rowId)));
        }
        await this.#ingestion.whenReleased().promise;
        pace = this.#installPacer == null ? null : await this.#installPacer.admit(this);
        if (bail()) return;

        const preparedRows = install.prepare(results);
        for (const row of preparedRows) {
          if (!install.installRow(row)) return;
        }

        // Mark unfetched RowHandles as covering this range so subscribe()
        // transitions from placeholder to dataFull(null).
        for (const [rowId, rh] of this.#rowHandles) {
          if (!seen.has(rowId)) {
            if (!this.#shouldMarkMissingRowFetched(rowId, rh)) continue;
            rh.markRangeFetched(utcRange);
          }
        }

        // Pre-create handles for CellProvider rows. These rows never appear in
        // server results, so their handles are otherwise only created on explicit
        // access. Ensuring they exist here lets evaluateFormulas evaluate them.
        if (this.#rows) {
          for (const [, row] of this.#rows.iter()) {
            const descriptor = row.descriptor;
            if (CellProvider.is(descriptor.dataOwner)) {
              this.rowHandle(RowNetworkKey.of(row.raw.id));
            }
          }
        }

      } finally {
        this.#leases.delete(lease);
        lease.release();
        observation?.release();
        if (retryRows.size > 0) this.#retryAfterUnownedInstall(utcRange, retryRows);
      }
      await this.#evaluateFormulas(naiveRange, staleIds);
      if (staleIds.size > 0) {
        this.#notifyChange(staleIds, naiveRange, { kind: "accessor" });
        this.#fireSyncStateChanges(staleIds);
      }
      // Rows the response first covered with no cells: their coverage is
      // announced as such ("coverage"), not as a cell change.
      if (coveredIds.size > 0) {
        this.#notifyChange(coveredIds, naiveRange, { kind: "coverage" });
        this.#fireSyncStateChanges(coveredIds);
      }
    } finally {
      pace?.settle();
    }
  }

  /**
   * The bulk-fetch observation for a request going out now over `range`
   * (`null`: a row's own read), held in `#observations` until its fetch or
   * `dispose()` releases it, whichever is first; the later release is a no-op.
   */
  #observe(range: Option<NaiveDate.Range>): Option<CellAccessor.BulkFetchObservation> {
    const taken = this.#captureBulkFetchObserver?.(range) ?? null;
    if (taken == null) return null;
    this.#observations.add(taken);
    return {
      receive: taken.receive,
      release: () => {
        if (this.#observations.delete(taken)) taken.release();
      },
    };
  }

  // ── Formula evaluation ──────────────────────────────────────────────────────

  /**
   * Evaluate formula rows in topological order after a data fetch.
   * Adds formula row IDs to the stale set.
   */
  /**
   * Re-evaluate formula rows that depend on a changed source row.
   * Uses the DependencyGraph to find transitive dependents, then
   * marks them stale and recomputes in topological order.
   */
  async #evaluateDependents(
    changedRowId: string,
    range: NaiveDate.Range,
    staleIds: Set<string>,
    residentRange: Option<NaiveDate.Range>,
  ): Promise<void> {
    if (this.#disposed || !this.#graph || !this.#ids) return;
    const localId = this.#ids.get(RowKey.of(changedRowId));
    if (!localId) return;

    const dependents = DependencyGraph.dependentsOnly(this.#graph, localId);
    if (dependents.size === 0) return;

    const handled = this.#residentFormulaRecompute?.(localId, residentRange ?? range, dependents) ?? null;
    if (this.#disposed) return;
    for (const depId of dependents) {
      const depKey = depId.toString();
      if (handled?.has(depId)) {
        staleIds.add(depKey);
        continue;
      }
      const rh = this.#rowHandles.get(RowNetworkKey.fromWire(depKey));
      if (!rh || rh.kind !== "formula" || !rh.computeRange) continue;
      await rh.refresh(range);
      staleIds.add(depKey);
    }
  }

  async #evaluateFormulas(
    range: NaiveDate.Range,
    staleIds: Set<string>,
  ): Promise<void> {
    if (!this.#graph || !this.#ids) return;

    const formulaHandles: [string, RowCellCache][] = [];
    for (const [id, rh] of this.#rowHandles) {
      if (rh.kind === "formula" && rh.computeRange) {
        formulaHandles.push([id, rh]);
      }
    }
    if (formulaHandles.length === 0) return;

    // Sort by dependency count (fewer deps first → topological order)
    const graph = this.#graph;
    const ids = this.#ids;
    formulaHandles.sort(([a], [b]) => {
      const aId = ids.resolve(RowKey.of(a));
      const bId = ids.resolve(RowKey.of(b));
      return graph.getDependencies(aId).size - graph.getDependencies(bId).size;
    });

    for (const [id, rh] of formulaHandles) {
      await rh.refresh(range);
      staleIds.add(id);
    }
  }

  async #ensurePersistedInner(
    rowId: RowNetworkKey,
    row: SheetRow,
    position: Option<Rational>,
  ): Promise<void> {
    const result = await this.#api.postResult("/api/sheets/{sheetKey}/row", {
      path: { sheetKey: this.#networkKey },
      body: Network.NewRowBody.make({
        // Canonical wire type, NOT the coarse `descriptor.id`. One descriptor
        // backs many wire types, so `descriptor.id` re-persisted divergent rows
        // under a bare/legacy form — a weather alias (id `"weather"`) was
        // rewritten as bare `type:"weather"` on every re-save, a straggler
        // factory. `raw.type` is the canonical form the row was hydrated as.
        type: row.raw.type,
        position: position ?? null,
        label: row.label ?? null,
        timeScale: row.timescaleSetting !== "month" ? row.timescaleSetting : null,
        attributes: row.raw.attributes ?? null,
        formula: row.formula ?? null,
        lookupKey: row.lookupKey ?? null,
        surface: null,
        id: null,
      }),
    });

    if (!result.isOk()) {
      const error = result.err();
      const reason = error?.reason ?? "";
      if (reason !== "lookup_key_conflict") {
        const parts = [error?.reason, error?.details]
          .filter((part): part is string => !!part && part.length > 0);
        const message = parts.join(": ");
        throw new Error(
          message || `POST /api/sheets/${this.#networkKey}/row failed with ${error?.code}`,
        );
      }
    }

    this.#ensuredLookupRows.add(rowId);
    this.#sheetFetcher.fetchedRange = null;
    this.#backend.storeRowMeta(
      RowId.of(rowId),
      SheetRowMeta.of(row.raw, SheetRowMeta.DETACHED),
      CellAccessor.#typeCardinality(row.raw.type),
    );
  }

  /** What `#bulkFetch`'s install transaction (`BulkInstall`) reads and writes through. */
  #bulkInstallPort(): BulkInstall.Port {
    return {
      networkKey: this.#networkKey,
      // If the sheet was switched away while the fetch was in flight, skip
      // the cascade; `bailedWhileInactive` makes reactivation re-fetch.
      bail: () => {
        if (this.#disposed) return true;
        if (this.#isActive) return false;
        this.#bailedWhileInactive = true;
        return true;
      },
      visibleRowIdOf: (rowData) => this.#visibleRowIdForBulkResult(rowData),
      rowHandle: (rowId) => this.rowHandle(rowId),
      accessKeyOf: (rowId) => this.#accessRouteFor(rowId).key,
      heldAt: (accessKey) => this.#rowHandles.get(accessKey) ?? null,
      effectiveMetadata: (raw, rowId) => this.#rows?.effectiveMetadata(raw, rowId) ?? null,
      storeRowMeta: (id, metadata) =>
        this.#backend.storeRowMeta(id, SheetRowMeta.of(metadata, SheetRowMeta.DETACHED), CellAccessor.#typeCardinality(metadata.type)),
      typeCardinality: (type) => CellAccessor.#typeCardinality(type),
      applySync: (rh, rowId, syncKeys, utcRange) => {
        this.#backend.applySync(rh, syncKeys, utcRange);
        if (!rh.onSyncStateChanged) {
          rh.onSyncStateChanged = () => {
            if (!this.#ids) return;
            const localId = this.#ids.resolve(RowKey.of(rowId));
            this.onSyncStateChange.update(localId);
          };
        }
      },
      observer: () => this.#bulkFetchObserver,
    };
  }

  #visibleRowIdForBulkResult(rowData: Network.SheetRowDataResponse): RowNetworkKey {
    // Responses may omit `lookupKey` (e.g. mock/ephemeral backends);
    // the row meta stored at parse time carries it.
    const lookupKey = rowData.lookupKey ??
      this.#backend.rowMeta(rowData.id)?.lookupKey ?? null;
    if (!lookupKey) return RowNetworkKey.of(rowData.id);

    // Route through the shared cell-access key rule in BOTH branches:
    // the registry may alias a lookup key to the row's UUID-form
    // localId (lookup->UUID promotion), and canonical (`:~`) rows
    // must install under their lookup-keyed shared handle either way
    // — or readers resolving that handle see an empty cache.
    const localId = this.#ids?.get(RowKey.of(lookupKey));
    const row = localId ? this.#rows?.get(localId) : null;
    if (localId && row?.lookupKey) {
      return RowNetworkKey.fromWire(cellAccessKey(localId.toString(), row.lookupKey));
    }
    return RowNetworkKey.fromWire(cellAccessKey(rowData.id.toString(), lookupKey));
  }

  #shouldMarkMissingRowFetched(rowId: RowNetworkKey, rh: RowCellCache): boolean {
    // Computed rows are absent from server bulk results by design. They become
    // covered only after their computeRange fills the derived cache for a range.
    if (rh.computeRange) return false;

    // Resolve dataOwner + lookupKey from the backend cache (fed by top-level
    // wire rows), falling back to the canonical `rowsSheet` authority for
    // client-expanded alias children the cache never saw.
    const cached = this.#backend.rowMeta(RowId.of(rowId));
    const meta = cached
      ? {
        type: cached.type,
        dataOwner: RowType.get(cached.type).dataOwner,
        lookupKey: cached.lookupKey ?? null,
      }
      : this.#resolvers?.rowMeta(rowId) ?? null;

    // Unknown to BOTH registries: a genuinely absent row → mark covered so the
    // placeholder settles (the legacy default for a true miss).
    if (meta == null) return true;

    // Rows that fetch their OWN cells per-row must NOT be vacuous-marked — the
    // bulk sheet response omits reserved/subcolumn rows, so marking them
    // "covered" lets vacuous coverage resolve readers EMPTY for a frame before
    // the per-row fetch lands (a flash). That covers provider/alias rows by
    // kind AND any row with a `lookupKey` (a per-row fetch-by-key source) —
    // critically `:~weather:{lid}`, whose dataOwner resolves to the generic
    // "user" (`data-source` → fallback) and so is NOT caught by the kind tests.
    // They settle via their own fetch instead (empty or not, it sets real
    // `fetchedRange`).
    if (meta.dataOwner === "provider") return false;
    if (AliasResolver.is(meta.dataOwner)) return false;
    if (meta.lookupKey != null) {
      // A lookup-keyed row normally settles via its OWN per-row fetch —
      // canonical (`:~`) rows are server-materialized off their key. But a
      // NON-canonical local lookup key on an ephemeral sheet (e.g. a seeded /
      // computed `:dbg:…` data-source row, or a `ComputedDataSource` whose
      // cells are engine-produced) has no server to fetch from: its cells live
      // only in the in-memory store, never appear in the bulk response, and no
      // per-row fetch will ever settle it. Mark it covered here or it shimmers
      // forever. Canonical keys still fall through to their real fetch.
      if (rh.ephemeral && !isCanonicalKey(meta.lookupKey)) return true;
      return false;
    }

    return true;
  }

  // ── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to cell data changes. Returns an unsubscribe function. The
   * listener receives the stale row ids and the date range the change is
   * known to cover — the fetched window for a fetch, null when the change
   * is not bounded by dates (a write, an explicit invalidation).
   */
  onChange(cb: CellAccessor.ChangeListener): Unsubscribe {
    let wrapped = this.#publicListeners.get(cb);
    if (wrapped == null) {
      wrapped = (rows, range) => cb(rows, range);
      this.#publicListeners.set(cb, wrapped);
    }
    this.#listeners.add(wrapped);
    // As with the original Set, any unsubscribe for this callback removes its
    // current registration, including a remove/re-register using the same cb.
    return () => {
      const current = this.#publicListeners.get(cb);
      if (current != null) this.#listeners.delete(current);
      this.#publicListeners.delete(cb);
    };
  }

  /** Internal relay certification; public callbacks retain exactly two arguments. */
  onChangeWithOrigin(cb: CellAccessor.OriginChangeListener): Unsubscribe {
    this.#listeners.add(cb);
    return () => { this.#listeners.delete(cb); };
  }

  /**
   * @internal Announce a core-owned native store install to sheet listeners.
   * Facts are already installed; this neither marks rows stale nor changes fetch coverage.
   */
  notifyNativeArrival(
    rowIds: Iterable<string>,
    range: NaiveDate.Range,
    certifiedDateRows: ReadonlySet<string>,
  ): void {
    if (this.#disposed) return;
    this.#notifyChange(rowIds, range, { kind: "native-store", certifiedDateRows });
  }

  // ── Lifecycle / Invalidation ──────────────────────────────────────────────

  /**
   * Reset the fetch cache so the next fetch makes a fresh HTTP call.
   */
  resetFetchCache(): void {
    this.#sheetFetcher.fetchedRange = null;
    for (const rh of this.#rowHandles.values()) {
      rh.fetcher.fetchedRange = null;
    }
  }

  /**
   * Mark a row's data as stale. Fires onChange listeners.
   */
  invalidateRow(rowId: RowNetworkKey): void {
    // By access key, as `rowHandle` mints: any id of a lookup-keyed row
    // reaches the one handle its cells live in.
    const rh = this.#rowHandles.get(this.#accessKeyFor(rowId));
    if (rh) {
      rh.markStale();
      this.#notifyChange([rowId], null, { kind: "accessor" });
    }
  }

  /**
   * Tell the change listeners that the rows `staleIds` (wire ids) changed
   * over `range`: each id as the client's `RowLocalId` (its registry's; an
   * accessor no sheet bound yet brands them in a registry of its own).
   *
   * Membership is fixed when the fan-out starts (one removed during it is
   * skipped, one added waits for the next), a throwing listener never stops
   * the others, and what they threw is reported out of band once all have
   * run — never into the fetch or write that changed the rows, so the
   * sync-state notification after it still fires and the fetch resolves.
   */
  #notifyChange(
    staleIds: Iterable<string>,
    range: Option<NaiveDate.Range>,
    origin: CellAccessor.ChangeOrigin,
  ): void {
    if (this.#listeners.size === 0) return;
    const ids = this.#ids ?? (this.#unboundIds ??= new RowLocalId.Registry());
    const rows = new Set<RowLocalId>();
    for (const id of staleIds) rows.add(ids.resolve(RowKey.of(id)));
    ListenerErrors.report(
      this.#listenerErrors,
      ListenerErrors.fanOut(
        [...this.#listeners],
        (cb) => this.#listeners.has(cb),
        (cb) => cb(rows, range, origin),
      ),
      "CellAccessor",
    );
  }

  /**
   * Remove a row's cached handle.
   */
  removeRow(rowId: RowNetworkKey): void {
    const rh = this.#rowHandles.get(rowId);
    if (rh) {
      rh.fetchBindings.unbind(this);
      rh.disposeAll();
      if (this.#contextRows.delete(rh)) rh.computeRange = null;
      this.#rowHandles.delete(rowId);
    }
  }

  /**
   * Release this accessor's hold on its row handles.
   *
   * Handles come from the client-scoped backend registry and may be
   * shared with other sheets and standalone `rows.open()` clients, so
   * closing one sheet MUST NOT `disposeAll()` them — that would clear
   * every other consumer's listeners (e.g. a standalone row's
   * `RowHandleEventsReader.onChange`) and leave them serving stale
   * reads. Instead, release only the wiring this accessor installed in
   * `rowHandle()` (`fetchFn`, `sheetFetch`, `invalidateSheetCache`): where
   * it is still the row's active wiring, the newest surviving sheet's is
   * reinstalled, or the backend's own fetch when no sheet holds the row
   * (`RowCellCache.fetchBindings`) — so a closed sheet is never kept alive by the
   * client-wide cache, and a survivor keeps its sheet fetch.
   * Per-subscription listeners are removed by their own
   * `SubscribeHandle.dispose()` on unbind. `removeRow` keeps full
   * disposal — there the row is genuinely deleted.
   */
  dispose(): void {
    this.#disposed = true;
    this.#residentFormulaRecompute = null;
    for (const lease of this.#leases) lease.release();
    this.#leases.clear();
    // A request still out ends its observation now (its sheet-wide read stops holding the rows' loads).
    for (const taken of [...this.#observations]) {
      this.#observations.delete(taken);
      taken.release();
    }
    this.#ingestion.release();
    const pacer = this.#installPacer;
    if (pacer != null) {
      this.#installPacer = null;
      pacer.release(this);
    }
    this.#listeners.clear();
    this.#publicListeners.clear();
    for (const rh of this.#rowHandles.values()) {
      rh.fetchBindings.unbind(this);
      if (this.#contextRows.has(rh)) {
        rh.computeRange = null;
        rh.disposeAll();
      }
    }
    this.#contextRows.clear();
    this.#rowHandles.clear();
    this.#handleKeyByLocalId.clear();
  }
}

export namespace CellAccessor {
  /** Native formulas evaluated by the owning model after an accepted mirror.
   * Unhandled IDs retain the accessor's legacy refresh behavior. */
  export type ResidentFormulaRecompute = (
    changedRowId: RowLocalId,
    range: NaiveDate.Range,
    dependents: ReadonlySet<RowLocalId>,
  ) => ReadonlySet<RowLocalId>;

  /** Where a bound row's cells live: the view of its sheet's cell store (`null`: none of the sheet's stores). */
  export type StoreViews = (accessKey: RowNetworkKey, localId: Option<RowLocalId>, rh: RowCellCache) => Option<CellStoreView>;

  /** What `CellAccessor.instrumentation()` hands a test. */
  export interface Instrumentation {
    /**
     * Register `rh` under the access key `key`, behind every open lease's
     * back — how an out-of-band handle swap looks to a parked response.
     */
    swapRowHandle(key: string, rh: RowCellCache): void;
  }

  /**
   * Reject an empty cell id. `""` is not "no id" — `null` is — and a blank
   * id would upsert every blank-id write onto one server cell.
   */
  export function requireCellId(where: string, id: Option<string>): void {
    if (id != null) requireNonEmptyKey(where, "id", id);
  }

  /**
   * Persist one cell (`POST /api/rows/{rowKey}/cells`), checked: a
   * rejected or unanswered write throws `CellWriteError`. `id` upserts by
   * `(row_id, id)`; `null` writes the date-keyed cell of a single-cardinality
   * row, or adds a cell to a multi-cardinality one (`CellTarget.add`: the
   * server mints its id, and the answer carries the cell). The single wire
   * write behind both row facades' date-keyed `write`; resolves the
   * server's answer.
   */
  export async function persistCell(
    api: TypedApi<Network.Routes>,
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
  ): Promise<Network.CellWriteResponse> {
    requireCellId("CellAccessor.persistCell", id);
    const body = Network.CellWriteRequest.make({ date: SemanticTime.fromPartialDate(date), data, id: id == null ? null : CellId.of(id), endDate: null });
    // after-failed-write: the wire write below the cell writers; every `await …persistCell(` caller recovers (the mirror rule).
    return await CellWriteError.check(
      data == null ? "clear-cell" : "write-cell",
      "POST",
      "/api/rows/{rowKey}/cells",
      () => api.postResult("/api/rows/{rowKey}/cells", { path: { rowKey: RowNetworkKey.fromWire(rowKey) }, body }),
    );
  }

  /**
   * Refuse a date-keyed address the row's cardinality cannot honour, before
   * anything is sent (`CellWriteError`, outcome `unaddressable`): an id-less
   * `date` address on a multi-cardinality row (its cells are told apart by
   * id — hold the cell, or `CellTarget.add` one); on a single-cardinality
   * row (one cell per column, which has no id — write `CellTarget.date`),
   * an `add` and any address that names an id (`CellTarget.keyed`,
   * `CellTarget.addWithId`, a held cell carrying an id): the server would
   * keep a second cell in the column that the row's one-cell cache cannot
   * hold. `id` is the id the address names (`add`: the id `addWithId`
   * chose, `null` for `CellTarget.add`). `step` / `verb` name the refused
   * write.
   */
  export function checkAddress(
    cardinality: CellCardinality,
    address: "date" | "add",
    id: Option<string>,
    step: CellWriteError.Step,
    verb: string,
  ): void {
    if (address === "date" && cardinality === "multi" && id == null) {
      throw unaddressable(
        step,
        `${verb}: a multi-cardinality row tells its cells apart by id, so a date names none of ` +
          "them — hold the cell (CellTarget.cell / CellTarget.keyed), or add one (CellTarget.add)",
      );
    }
    if (cardinality !== "single") return;
    if (address === "add") {
      throw unaddressable(
        step,
        `${verb}: a single-cardinality row has one cell per column — write it with CellTarget.date`,
      );
    }
    if (id != null) {
      throw unaddressable(
        step,
        `${verb}: a single-cardinality row has one cell per column and it has no id, so cell id ` +
          `"${id}" names none of its cells — write the column's cell with CellTarget.date ` +
          "(an id-keyed address, CellTarget.keyed / CellTarget.addWithId, is a multi-cardinality row's)",
      );
    }
  }

  /** The `unaddressable` refusal of a cell write (`step`), nothing sent. */
  function unaddressable(step: CellWriteError.Step, details: string): CellWriteError {
    return new CellWriteError({
      step,
      method: "POST",
      path: "/api/rows/{rowKey}/cells",
      failure: { outcome: "unaddressable" },
      details,
    });
  }

  /**
   * Mirror an accepted date-keyed write into the row's handle, stamped with
   * `actor`. The write was already accepted, so this never throws: when the
   * row's cardinality no longer admits the address (an id-less write on a
   * row that turned multi-cardinality while it was out, an id-keyed one on
   * a row that turned single-cardinality), the row is marked stale instead
   * and its next read re-reads it.
   */
  export function mirrorWrite(
    rh: RowCellCache,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    actor: Actor,
  ): void {
    if ((id == null) === (rh.cardinality === "multi")) {
      rh.markStale();
      return;
    }
    rh.applyCellWriteLocal(date, data, actor, id);
  }

  /**
   * Reconcile a cell write's answer into the row's handle: drop `removeId`
   * (a replaced or deleted cell) and upsert the cells the server returned,
   * stamped with the writer's `actor` (`CellHandle.onData`'s
   * `change.actor`). The write was already accepted, so this never throws: a returned cell
   * the row cannot place (one without the id its lane is keyed by — a
   * server contract violation) is left out and the row marked stale, so its
   * next read re-reads it.
   */
  export function reconcileAnswer(
    rh: RowCellCache,
    removeId: Option<string>,
    cells: readonly Network.SheetCell[],
    actor: Actor,
  ): void {
    const admitted = cells.filter((cell) => RowData.admits(cell, rh.cardinality));
    rh.applyCustomWriteResponse(removeId == null ? null : CellId.of(removeId), admitted, actor);
    if (admitted.length < cells.length) rh.markStale();
  }

  /**
   * The `CellWriteError` a refused durable cell write rejects with: status
   * `0` is the executors' "no answer" (`"unanswered"`), a 2xx answer that was
   * not the route's `"malformed"`, any other status a refusal
   * (`DurableHandoffs.verdict`).
   */
  export function durableRefusal(
    step: "write-cell" | "clear-cell",
    failure: DebouncedRequests.Failure,
  ): CellWriteError {
    const verdict = DurableHandoffs.verdict(failure);
    return new CellWriteError({
      step,
      method: "POST",
      path: "/api/rows/{rowKey}/cells",
      failure: verdict,
      reason: failure.reason ?? "",
      details: failure.details ?? "",
    });
  }

  /** The debounce key of a durable cell write — one per `(row, date, id)`,
   *  so rapid edits of one cell coalesce. */
  export function durableCellKey(
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    id: Option<string>,
  ): string {
    return `cell:${rowKey}:${date.toString()}:${id ?? ""}`;
  }

  /**
   * Hand one cell write to the API's debounce executor (0 ms: a durable
   * handoff, not a delay), keyed by `durableCellKey`. Resolves once the
   * execution that carried it finished; a refusal is reported to the
   * client's durable-failure seam naming `requestId`, not through this
   * promise. The durable twin of `persistCell`.
   */
  export async function persistCellDurable(
    api: TypedApi<Network.Routes>,
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    requestId: Option<string>,
  ): Promise<void> {
    requireCellId("CellAccessor.persistCellDurable", id);
    await api.debounce.post(
      "/api/rows/{rowKey}/cells",
      {
        path: { rowKey: RowNetworkKey.fromWire(rowKey) },
        body: Network.CellWriteRequest.make({
          date: SemanticTime.fromPartialDate(date),
          data,
          id: id == null ? null : CellId.of(id),
          endDate: null,
        }),
        options: {
          key: durableCellKey(rowKey, date, id),
          ...(requestId != null ? { requestId } : {}),
        },
      },
      0,
    );
  }

  /** A row's classification, as the fetch/coverage decision sites read it. */
  export type RowMeta = {
    readonly type: string;
    readonly dataOwner: DataOwner;
    readonly lookupKey: Option<string>;
    readonly isFormula: boolean;
  };

  /**
   * The owning sheet's row resolvers, by ACCESS key — rows-side first
   * (Row-Metadata-Authority P3 "one resolver"). The decision sites consult
   * them for client-expanded alias children (e.g. `:~weather:{lid}`) that the
   * renderer shows but `backend.rowMeta` / the `SheetRow` registry — fed only
   * from top-level wire rows — never registered. Bound by `SheetHandle.parse`.
   */
  export type Resolvers = {
    /** Child row ids a row renders (warmed when its bulk fetch came back empty). */
    childrenOf(rowId: RowNetworkKey): string[];
    /** A row's classification; `null` when unknown. */
    rowMeta(rowId: RowNetworkKey): Option<RowMeta>;
    /** Every rendered row sharing the key; `null` defers to the interned id. */
    localIds(rowKey: RowNetworkKey): Option<RowLocalId[]>;
  };

  /**
   * A cell-data change: the stale rows (the client's `RowLocalId`s, compared
   * with `row.id`) and the date range the change is known to cover (end
   * exclusive) — the fetched window for a fetch, null when the change is not
   * bounded by dates (a write, an explicit invalidation), which consumers
   * treat as the whole row.
   */
  export type ChangeListener = (staleRows: ReadonlySet<RowLocalId>, range: Option<NaiveDate.Range>) => void;
  /**
   * Where a change came from. `"native-store"`: only actual dated physical
   * changes qualify; timepoints retain row-range relay. `"coverage"`: a read
   * first covered the range for these rows and carried no cells (no cell
   * changed; the sheet relays it as `RowRangeCovered`).
   */
  export type ChangeOrigin = { readonly kind: "accessor" } | { readonly kind: "coverage" } | {
    readonly kind: "native-store";
    readonly certifiedDateRows: ReadonlySet<string>;
  };
  export type OriginChangeListener = (
    staleRows: ReadonlySet<RowLocalId>, range: Option<NaiveDate.Range>, origin: ChangeOrigin,
  ) => void;
  /**
   * One parked bulk response's hold on the handles it will install into: the
   * exact `RowCellCache` objects and the revision captured on each, so the
   * release never depends on a map that `dispose()` or a rekey may have
   * changed since. Idempotent.
   */
  export class Lease {
    readonly #handles = new Map<string, { rh: RowCellCache; rev: number }>();
    #released = false;

    adopt(rowId: RowNetworkKey, rh: RowCellCache): void {
      if (this.#released || this.#handles.has(rowId)) return;
      this.#handles.set(rowId, { rh, rev: rh.captureWriteRevision() });
    }

    /**
     * A promotion moved `winner` from `from` to `to` (`rekeyOneHandle` keeps
     * the populated source handle and disposes any distinct handle already
     * under `to`). Ownership follows the winner with its original freshness
     * boundary; a displaced destination capture is released. A winner this
     * lease never captured leaves `to` unowned, which `sinceFor` reports as
     * not installable.
     */
    rekey(from: string, to: string, winner: RowCellCache): void {
      if (this.#released) return;
      const displaced = this.#handles.get(to);
      if (displaced != null && displaced.rh !== winner) {
        displaced.rh.releaseWriteRevision(displaced.rev);
        this.#handles.delete(to);
      }
      const source = this.#handles.get(from);
      if (source == null) return;
      this.#handles.delete(from);
      if (source.rh === winner) this.#handles.set(to, source);
      else source.rh.releaseWriteRevision(source.rev);
    }

    /**
     * The revision to install `rowId` against, or `null` when this lease does
     * not own `rh` — then the snapshot must not be installed into it.
     */
    sinceFor(rowId: RowNetworkKey, rh: RowCellCache): Option<number> {
      const e = this.#handles.get(rowId);
      return e != null && e.rh === rh ? e.rev : null;
    }

    release(): void {
      if (this.#released) return;
      this.#released = true;
      for (const { rh, rev } of this.#handles.values()) rh.releaseWriteRevision(rev);
      this.#handles.clear();
    }
  }

  /** One row a bulk response installed, as the bulk observer receives it (`BulkInstall.FetchedRow`). */
  export type BulkFetchedRow = BulkInstall.FetchedRow;

  export type BulkFetchObserver = BulkInstall.FetchObserver;

  /** Response-time ownership for a second store populated by the observer. */
  export type BulkFetchObservation = BulkInstall.Observation;
}

